import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetTrigger, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import {
  Phone,
  Mail,
  Menu,
  Home,
  PiggyBank,
  TrendingUp,
  HandHeart,
  Users,
  MapPin,
  BookOpen,
  X,
} from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/logo.png";
import logoWebP from "@/assets/logo.webp";

const navItems = [
  { title: "Startseite", url: "/", icon: Home },
  { title: "Leibrente", url: "/leibrente", icon: PiggyBank },
  { title: "Teilverkauf", url: "/teilverkauf", icon: TrendingUp },
  { title: "Rückmiete", url: "/rueckmiete", icon: Home },
  { title: "Nießbrauch", url: "/niessbrauch", icon: HandHeart },
  { title: "Wohnrecht", url: "/wohnrecht", icon: Users },
  { title: "Seniorenfinanzierung", url: "/seniorenfinanzierung", icon: MapPin },
  { title: "Erfolgsgeschichten", url: "/erfolgsgeschichten", icon: BookOpen },
];

const Header = () => {
  const [open, setOpen] = useState(false);

  return (
    <header className="bg-background border-b-2 border-border sticky top-0 z-40 shadow-soft">
      <div className="container-comfortable py-3 md:py-4">
        <div className="flex items-center justify-between gap-3">
          {/* Logo + Title */}
          <Link to="/" className="flex items-center gap-3 min-w-0 flex-shrink">
            <picture>
              <source srcSet={logoWebP} type="image/webp" />
              <img
                src={logo}
                alt="Rente Plus Immobilie"
                className="h-12 md:h-14 lg:h-16 w-auto object-contain flex-shrink-0"
                width="64"
                height="64"
                decoding="async"
              />
            </picture>
            <div className="hidden sm:block min-w-0">
              <div className="font-heading font-bold text-primary text-lg md:text-xl lg:text-2xl leading-tight truncate">
                Eigenheimverrentung
              </div>
              <div className="text-sm md:text-base text-muted-foreground hidden md:block">
                Ihr Weg zu finanzieller Sicherheit
              </div>
            </div>
          </Link>

          {/* Right side actions */}
          <div className="flex items-center gap-2 md:gap-3">
            {/* Anrufen — always visible, primary action */}
            <a
              href="tel:089628246446"
              className="inline-flex items-center gap-2 bg-accent text-accent-foreground font-semibold rounded-lg px-3 md:px-5 py-3 md:py-3.5 text-base md:text-lg shadow-button hover:bg-accent-hover transition-colors min-h-[3rem]"
              aria-label="Jetzt anrufen: 089 62 82 46 46"
            >
              <Phone className="h-5 w-5 md:h-6 md:w-6" aria-hidden="true" />
              <span className="hidden sm:inline">Anrufen</span>
              <span className="hidden lg:inline">089 62 82 46 46</span>
            </a>

            {/* Menu trigger */}
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button
                  className="inline-flex items-center gap-2 border-2 border-primary text-primary font-semibold rounded-lg px-3 md:px-5 py-3 md:py-3.5 text-base md:text-lg hover:bg-primary hover:text-primary-foreground transition-colors min-h-[3rem]"
                  aria-label="Menü öffnen"
                >
                  <Menu className="h-6 w-6 md:h-7 md:w-7" aria-hidden="true" />
                  <span className="hidden sm:inline">Menü</span>
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-full sm:max-w-md p-0 bg-background flex flex-col"
              >
                <SheetHeader className="px-6 py-5 border-b-2 border-border flex-shrink-0">
                  <SheetTitle className="text-2xl font-heading text-primary text-left">
                    Verrentungsmodelle
                  </SheetTitle>
                </SheetHeader>

                <nav className="px-4 py-4 overflow-y-auto flex-1" aria-label="Hauptnavigation">
                  <ul className="space-y-2">
                    {navItems.map((item) => {
                      const Icon = item.icon;
                      return (
                        <li key={item.url}>
                          <Link
                            to={item.url}
                            onClick={() => setOpen(false)}
                            className="flex items-center gap-4 p-4 rounded-xl border-2 border-border text-primary text-lg font-medium hover:bg-secondary hover:border-accent transition-colors min-h-[3.5rem]"
                          >
                            <span className="bg-accent/15 p-2.5 rounded-lg flex-shrink-0">
                              <Icon className="h-6 w-6 text-accent" aria-hidden="true" />
                            </span>
                            <span>{item.title}</span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>

                  <div className="mt-6 pt-6 border-t-2 border-border space-y-3">
                    <a
                      href="tel:089628246446"
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-center gap-3 bg-accent text-accent-foreground font-semibold rounded-xl p-4 text-lg shadow-button min-h-[3.5rem]"
                    >
                      <Phone className="h-6 w-6" aria-hidden="true" />
                      089 62 82 46 46
                    </a>
                    <a
                      href="mailto:info@renteplusimmobilie.de"
                      onClick={() => setOpen(false)}
                      className="flex items-center justify-center gap-3 border-2 border-primary text-primary font-semibold rounded-xl p-4 text-lg min-h-[3.5rem]"
                    >
                      <Mail className="h-6 w-6" aria-hidden="true" />
                      E-Mail schreiben
                    </a>
                  </div>

                  <div className="mt-6 flex flex-wrap gap-x-4 gap-y-2 text-base text-muted-foreground">
                    <Link to="/impressum" onClick={() => setOpen(false)} className="hover:text-primary underline">Impressum</Link>
                    <Link to="/datenschutz" onClick={() => setOpen(false)} className="hover:text-primary underline">Datenschutz</Link>
                    <Link to="/agb" onClick={() => setOpen(false)} className="hover:text-primary underline">AGB</Link>
                  </div>
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
